import { useCallback, useEffect, useState } from 'react';
import type {
  OpenChatPayload,
  SendJobPayload,
  SendMessagePayload,
  StartChatTarget,
} from '@chat-emp/shared';

import { emitToHost, onBridgeMessage } from '../lib/bridge.js';
import { sendJobApi, startChatApi } from '../lib/facade.js';

export interface OpenRequest {
  requestId: number;
  /** View / Reply — open this thread directly, no compose. */
  channelId?: string;
  /** Start — nothing exists yet; compose against this. */
  target?: StartChatTarget;
  draft?: string;
}

/**
 * Narrow a host payload to a usable target. Everything descriptive is
 * host-supplied by design, so the only question here is whether the fields the
 * BFF needs are present — not whether their values are plausible.
 */
function toTarget(p: Record<string, unknown>): StartChatTarget | null {
  const applicant = p.applicant as StartChatTarget['applicant'] | undefined;
  const company = p.company as StartChatTarget['company'] | undefined;
  const source = p.source;
  if (!applicant?.id || !applicant.name || !company?.name || typeof source !== 'string') {
    return null;
  }
  return {
    applicant,
    company,
    source,
    ...(p.sourceMeta ? { sourceMeta: p.sourceMeta as Record<string, unknown> } : {}),
    ...(p.job ? { job: p.job as { id: string } } : {}),
  };
}

/**
 * The host's start-chat requests, and the headless send.
 *
 * `open-chat` becomes UI; `send-message` deliberately does not. The headless
 * path exists so a host can send without showing anything, and it is answered
 * over the bridge rather than rendered — which is why it lives here rather than
 * in the panel, whose whole job is to be visible.
 *
 * A malformed `send-message` is still answered. The host is holding a promise
 * on the other side of it, and a silent drop would hang that promise and its
 * toast forever.
 */
export function useStartChatRequest(): { request: OpenRequest | null; clear: () => void } {
  const [request, setRequest] = useState<OpenRequest | null>(null);

  useEffect(() => {
    return onBridgeMessage((env) => {
      if (env.type === 'open-chat') {
        const p = (env.payload ?? {}) as Partial<OpenChatPayload> & Record<string, unknown>;
        if (typeof p.requestId !== 'number') return;
        const draft = typeof p.draft === 'string' ? p.draft : undefined;

        if (typeof p.channelId === 'string' && p.channelId) {
          setRequest({
            requestId: p.requestId,
            channelId: p.channelId,
            ...(draft ? { draft } : {}),
          });
          return;
        }
        const target = toTarget(p);
        if (!target) return; // neither a channel nor a usable target
        setRequest({ requestId: p.requestId, target, ...(draft ? { draft } : {}) });
        return;
      }

      if (env.type === 'send-job') {
        const p = (env.payload ?? {}) as Partial<SendJobPayload> & Record<string, unknown>;
        if (typeof p.requestId !== 'number') return;
        const requestId = p.requestId;
        const channelId = typeof p.channelId === 'string' ? p.channelId : '';
        const jobId = typeof p.jobId === 'string' ? p.jobId : '';
        if (!channelId || !jobId) {
          emitToHost('send-result', { requestId, ok: false, code: 'bad_request' });
          return;
        }
        const text = typeof p.text === 'string' && p.text.trim() ? p.text : undefined;
        void sendJobApi({ channelId, jobId, ...(text ? { text } : {}) })
          .then(({ channelId }) => emitToHost('send-result', { requestId, ok: true, channelId }))
          .catch((err: unknown) => {
            const code = (err as { code?: string })?.code ?? 'upstream_error';
            emitToHost('send-result', { requestId, ok: false, code });
          });
        return;
      }

      if (env.type === 'send-message') {
        const p = (env.payload ?? {}) as Partial<SendMessagePayload> & Record<string, unknown>;
        if (typeof p.requestId !== 'number' || typeof p.text !== 'string' || !p.text) return;
        const requestId = p.requestId;
        const target = toTarget(p);
        if (!target) {
          emitToHost('send-result', { requestId, ok: false, code: 'bad_request' });
          return;
        }
        void startChatApi({
          applicant: target.applicant,
          company: target.company,
          source: target.source,
          ...(target.sourceMeta ? { sourceMeta: target.sourceMeta } : {}),
          ...(target.job ? { job: target.job } : {}),
          text: p.text,
        })
          .then(({ channelId }) => emitToHost('send-result', { requestId, ok: true, channelId }))
          .catch((err: unknown) => {
            const code = (err as { code?: string })?.code ?? 'upstream_error';
            emitToHost('send-result', { requestId, ok: false, code });
          });
      }
    });
  }, []);

  const clear = useCallback(() => setRequest(null), []);
  return { request, clear };
}
